import AgencyHelper, { AgencyConstants } from 'features/Agency/agencyHelper';
import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import KTBSProgressBar from 'general/components/OtherKeenComponents/KTBSProgressBar';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import CustomDateRangePicker, {
  dateFilter,
  getDateFilterOption,
} from 'general/components/Others/CustomDateRangePicker';
import BarChart from 'general/components/quybka/Charts/BarChart';
import DoughnutChartJS from 'general/components/quybka/Charts/DoughnutChartJS';
import HalfCircleChart from 'general/components/quybka/Charts/HalfCircleChart';
import AppColors from 'general/constants/AppColors';
import AppResource from 'general/constants/AppResource';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import AgencyDashboardHeaderView from '../../components/AgencyDashboardHeaderView';
import KTDropdownFilter from 'general/components/OtherKeenComponents/KTDropdownFilter';
import DropdownFilterTable from 'general/components/DropdownFilterTable';
import KTFormInput, { KTFormInputType } from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import { useSelector } from 'react-redux';

SaleDashboardScreen.propTypes = {
  agencyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function SaleDashboardScreen({ agencyId, ...props }) {
  // MARK: ---- PROPERTIES ----
  const { user } = useSelector((state) => state.auth);
  const [agencyDetail, setAgencyDetail] = useState(null);
  const isRoleSale = useMemo(() => {
    return agencyDetail?.primaryRole === 'SALE'
  }, [agencyDetail])
  const isAdminAgency = useMemo(() => {
    return !agencyDetail?.parentAgencyId
  }, [agencyDetail])
  useEffect(() => {
    if (Utils.isValid(agencyId)) {
      AgencyHelper.API.requestAgencyDetail(agencyId).then((response) => {
        setAgencyDetail(response);
      });
    }
  }, [agencyId]);
  const [chartData, setChartData] = useState({});
  const [chartFilter, setChartFilter] = useState({
    from: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[0].format('YYYY-MM-DD'),
    to: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[1].format('YYYY-MM-DD'),
    selectedValue: dateFilter.baMuoiNgayGanNhat,
  });
  // thong ke tai khoan
  const [agencyStatistic, setAgencyStatistic] = useState({});
  const [monthlySaleData, setMonthlySaleData] = useState({});
  const [monthlySaleDataCommission, setMonthlySaleDataCommission] = useState({});
  useEffect(() => {
    ChargeHelper.API.Dashboard.requestGetDailyData({
      from: chartFilter.from,
      to: chartFilter.to,
    }).then((res) => {
      setChartData(res ?? {});
    });
  }, [chartFilter]);
  const [agencyStatisticFilter, setAgencyStatisticFilter] = useState({
    status: 'NORMAL',
    parentAgencyId: agencyId,
    from: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[0].format('YYYY-MM-DD'),
    to: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[1].format('YYYY-MM-DD'),
    selectedValue: dateFilter.baMuoiNgayGanNhat,
  });
  useEffect(() => {
    ChargeHelper.API.Dashboard.requestGetAgencyStatistics(agencyStatisticFilter).then((res) => {
      setAgencyStatistic(res ?? {});
    });
  }, [agencyId, agencyStatisticFilter])
  const [dateFilterMonthlySale, setDateFilterMonthlySale] = useState(moment().format('YYYY-MM'));
  useEffect(() => {
    if (Utils.isValid(agencyId)) {
      AgencyHelper.API.requestGetAgencyMonthlySale({
        agencyId: agencyId,
        month: dateFilterMonthlySale,
      }).then((res) => {
        setMonthlySaleData(res ?? {});
      });
    }
  }, [agencyId, dateFilterMonthlySale]);
  useEffect(() => {
    if (Utils.isValid(agencyId)) {
      AgencyHelper.API.requestGetMonthlySaleCommission({
        agencyId: agencyId,
        month: dateFilterMonthlySale,
      }).then((res) => {
        setMonthlySaleDataCommission(res ?? {});
      });
    }
  }, [agencyId, dateFilterMonthlySale]);
  // doi soat hang thang (hoa hong da nhan)
  const [saleReconciliationList, setSaleReconciliationList] = useState([]);
  const [formatSaleReconciliationList, setFormatSaleReconciliationList] = useState([]);
  const [dateFilterReconciliation, setDateFilterReconciliation] = useState(String(moment().year()));
  // hoa hong chua ky doi soat
  const [pendingCommissions, setPendingCommissions] = useState([]);

  useEffect(() => {
    AgencyHelper.API.requestGetReconciliationList({
      agencyId: agencyId,
      status: AgencyConstants.AgencyReconciliationStatus.AGENCY_SIGNED,
      forRole: 'SALE',
      status: 'COMPLETED',
    }).then((res) => {
      setSaleReconciliationList(res ?? []);
    });
  }, [agencyId]);

  useEffect(() => {
    if (!Utils.isValid(agencyId)) return;
    AgencyHelper.API.requestGetReconciliationList({
      agencyId: agencyId,
      forRole: 'SALE',
    }).then((res) => {
      const pending = (res?.reconciliations ?? []).filter(
        (item) => item.status !== AgencyConstants.AgencyReconciliationStatus.AGENCY_SIGNED
      ).sort((a, b) => {
        const [mA, yA] = a.month.split('-').map(Number);
        const [mB, yB] = b.month.split('-').map(Number);
        return yB !== yA ? yB - yA : mB - mA;
      });
      setPendingCommissions(pending);
    });
  }, [agencyId]);

  useEffect(() => {
    const formattedSaleReconciliationList = saleReconciliationList?.reconciliations
      ?.filter((reconciliation) => {
        return reconciliation?.month?.split('-')[0] === String(dateFilterReconciliation);
      })
      ?.sort((a, b) => {
        const [monthA, yearA] = a.month.split('-').map(Number);
        const [monthB, yearB] = b.month.split('-').map(Number);

        if (yearA !== yearB) {
          return yearA - yearB;
        }
        return monthA - monthB;
      }) || [];

    setFormatSaleReconciliationList(formattedSaleReconciliationList);
  }, [saleReconciliationList, dateFilterReconciliation]);
  // doanh thu theo ngay
  const aggregateRevenueByDate = (investor, group, chargePoint, coinvestor) => {
    const allDates = Utils.generateDateRange(chartFilter?.from, chartFilter?.to);

    const result = allDates?.map(date => {
      const investorItem = investor?.find(item => item.date === date);
      const investorAmount = investorItem ? investorItem.kpiValue ?? 0 : 0;
      const groupItem = group?.find(item => item.date === date);
      const groupAmount = groupItem ? groupItem.kpiValue ?? 0 : 0;
      const chargePointItem = chargePoint?.find(item => item.date === date);
      const chargePointAmount = chargePointItem ? chargePointItem.kpiValue ?? 0 : 0;
      const coinvestorItem = coinvestor?.find(item => item.date === date);
      const coinvestorAmount = coinvestorItem ? coinvestorItem.kpiValue ?? 0 : 0;

      const totalAmount = investorAmount + groupAmount + chargePointAmount + coinvestorAmount;

      return { date, amount: totalAmount };
    });

    return result;
  }
  const getRevenueData = (type) => {
    switch (type) {
      case ChargeConstants.DashboardRevenueType.GROUP:
        return aggregateRevenueByDate(null, chartData.groupSaleByDate, null);
      case ChargeConstants.DashboardRevenueType.INVESTOR:
        return aggregateRevenueByDate(chartData.investorSaleByDate, null, null);
      case ChargeConstants.DashboardRevenueType.CHARGEPOINT:
        return aggregateRevenueByDate(null, null, chartData.chargePointSaleByDate);
      case ChargeConstants.DashboardRevenueType.COINVESTOR:
        return aggregateRevenueByDate(chartData.coinvestorSaleByDate, null, null);
      default:
        return aggregateRevenueByDate(chartData.investorSaleByDate, null, null, chartData.coinvestorSaleByDate);
    }
  }
  const revenueTitleRef = useRef();
  const revenueBodyRef = useRef();
  const [revenueType, setRevenueType] = useState('');
  const [revenueFilter, setRevenueFilter] = useState({ ...chartFilter, revenueType: revenueType });
  const chartRevenueEl = useMemo(() => {
    const revenueByDate = getRevenueData(revenueType) ?? [];
    let labels = revenueByDate.map((item) => Utils.formatDateTimeToString(item.date, 'DD'));
    let data = revenueByDate.map((item) => item.amount ?? 0);
    if (revenueByDate.length > 31) {
      const groupedData = _.groupBy(revenueByDate, (item) => moment(item.date).format('MM/YYYY'));
      labels = Object.keys(groupedData);
      data = labels.map((label) => {
        const total = groupedData[label].reduce((sum, item) => sum + item.amount, 0);

        return total;
      });
    }
    const revenueTotal = data.reduce((sum, item) => _.toNumber(sum ?? 0) + _.toNumber(item ?? 0), 0);

    return (
      <div className='h-100'>
        {UIHelper.buildCard(
          <div>
            <div
              ref={revenueTitleRef}
              className="px-4 pt-4 pb-4 d-flex align-items-center justify-content-between"
            >
              {/* title */}
              <div className="d-flex flex-column">
                <div className="d-flex align-items-center gap-2">
                  <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                    Doanh thu bán hàng{' '}
                  </span>
                  <KTTooltip
                    text="Với đơn hàng nhượng quyền, liên kết và bán trụ sạc: Doanh thu được tính dựa trên giá trị sản phẩm trụ sạc đã bán trước thuế, không tính giá trị trang thiết bị & dịch vụ có trong đơn hàng."
                    textHeader="Doanh thu bán hàng"
                    isHtmlContent
                  >
                    <i className="fa-regular fa-info-circle" style={{ color: AppColors.primary }} />
                  </KTTooltip>
                </div>
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                  Tổng:{' '}
                  <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                    {Utils.formatNumber(revenueTotal ?? 0)} VNĐ
                  </span>
                </span>
              </div>
              {/* actions */}
              <DropdownFilterTable
                isLightButton={true}
                toggleElementBorderRadius={10}
                filterComponent={[
                  <div className="col-12 p-0">
                    <KTDropdownFilter
                      label="Loại doanh thu"
                      filterKey="revenueType"
                      options={[
                        { name: 'Tất cả', value: '' },
                        ...ChargeHelper.Data.saleDashboardRevenueTypeOptions,
                      ]}
                      selectedValue={revenueFilter.revenueType}
                      handleChange={(value) => {
                        setRevenueFilter({ ...revenueFilter, revenueType: value });
                      }}
                    />
                  </div>,
                  <div className="col-12 p-0">
                    <label htmlFor="filterByDate" className="mb-0 mr-2 font-weight-bolder">
                      Khoảng thời gian thống kê
                    </label>
                    <CustomDateRangePicker
                      alignRight={true}
                      toggleFontWeight="600"
                      toggleElementBorderRadius={5}
                      initialValue={revenueFilter.selectedValue}
                      onPressSave={(selectedValue, startDate, endDate) => {
                        setRevenueFilter({
                          ...revenueFilter,
                          from: startDate,
                          to: endDate,
                          selectedValue: selectedValue,
                        });
                      }}
                    />
                  </div>
                ]}
                resetDefaultTable={() => {
                  const updateChartFilter = {
                    ...chartFilter,
                    from: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[0].format('YYYY-MM-DD'),
                    to: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[1].format('YYYY-MM-DD'),
                    selectedValue: dateFilter.baMuoiNgayGanNhat,
                  };
                  setRevenueFilter({ ...updateChartFilter, revenueType: '' });
                  setRevenueType('');
                  setChartFilter(updateChartFilter);
                }}
                saveFilter={() => {
                  const { revenueType, ...dateFilter } = revenueFilter;
                  setChartFilter(dateFilter);
                  setRevenueType(revenueType);
                }}
                isDefaultFilter={Utils.checkDefaultFilter(
                  ['selectedValue', 'from', 'to'],
                  { ...chartFilter, revenueType },
                  dateFilter.baMuoiNgayGanNhat,
                )}
              />
            </div>
            <div ref={revenueBodyRef} className="row m-0 p-0">
              <div className="pb-4 px-0">
                <div
                  className='px-2'
                  style={{
                    height: isRoleSale ? '200px' : '256px',
                  }}
                >
                  <BarChart
                    title="Doanh thu"
                    unit="VNĐ"
                    labels={labels}
                    data={data}
                    color="rgba(48, 189, 111, 0.4)"
                    hoverColor="rgba(48, 189, 111, 1)"
                    showLine={true}
                  />
                </div>
              </div>
            </div>
          </div>,
          false,
          false,
          true,
          true
        )}
      </div>
    );
  }, [chartData, chartFilter, revenueFilter, revenueType, isRoleSale, monthlySaleData, agencyDetail]);
  const agencyStatisticCardEl = useMemo(() => {
    return (
      <div className='h-100'>
        {UIHelper.buildCard(
          <div>
            <div className='d-flex align-items-center justify-content-between mr-4'
              style={{
                height: `${revenueTitleRef?.current?.offsetHeight}px`
              }}
            >
              {UIHelper.buildCardHeader('Số lượng đối tác', false, null, false, true)}
              <CustomDateRangePicker
                alignRight={true}
                toggleFontWeight="600"
                toggleElementBorderRadius={10}
                initialValue={agencyStatisticFilter.selectedValue}
                onPressSave={(selectedValue, startDate, endDate) => {
                  setAgencyStatisticFilter({
                    from: startDate,
                    to: endDate,
                    selectedValue: selectedValue,
                  });
                }}
              />
            </div>
            <div className="px-4 pb-4 d-flex align-items-center gap-3"
              style={{
                // height: `${revenueBodyRef?.current?.offsetHeight}px`
              }} >
              <div
                className=""
                style={{
                  height: '200px',
                  width: '200px',
                }}
              >
                <DoughnutChartJS
                  showTitle={false}
                  showLegend={false}
                  labels={['NĐT nhượng quyền', 'NĐT liên kết']}
                  datasets={[
                    {
                      data: [
                        agencyStatistic[AgencyConstants.AgencyRole.INVESTOR] ?? 0,
                        agencyStatistic[AgencyConstants.AgencyRole.COINVESTOR] ?? 0,
                      ],
                      borderColor: [
                        'rgba(255, 255, 255, 1)',
                        'rgba(255, 255, 255, 1)',
                      ],
                      backgroundColor: [
                        'rgba(50, 173, 230, 1)',
                        'rgba(221, 75, 146, 1)',
                      ],
                      borderWidth: 3,
                    },
                  ]}
                />
              </div>
              <div className="flex-fill">
                {UIHelper.buildDoughnutChartLegend([
                  {
                    title: 'Tổng số NĐT nhượng quyền',
                    color: 'rgba(50, 173, 230, 1)',
                    value: agencyStatistic[AgencyConstants.AgencyRole.INVESTOR] ?? 0,
                  },
                  {
                    title: 'Tổng số NĐT liên kết',
                    color: 'rgba(221, 75, 146, 1)',
                    value: agencyStatistic[AgencyConstants.AgencyRole.COINVESTOR] ?? 0,
                  },
                  {
                    title: 'Tổng số:',
                    value: _.sum([
                      agencyStatistic[AgencyConstants.AgencyRole.INVESTOR] ?? 0,
                      agencyStatistic[AgencyConstants.AgencyRole.COINVESTOR] ?? 0,
                    ]),
                  },
                ])}
              </div>
            </div>
          </div>,
          false,
          false,
          true,
          true
        )}
      </div>
    )
  }, [agencyStatistic, agencyStatisticFilter])
  const kpiSaleCardEl = useMemo(() => {
    return (
      <div className="h-100">
        {UIHelper.buildCard(
          <div
            className="mb-0 px-0 d-flex flex-column h-100"
          >
            {/* title */}
            <div className="d-flex justify-content-between align-items-center px-5 py-4"
              style={{
                borderBottom: `1px solid ${AppColors.gray[300]}`,
              }}
            >
              <div className="d-flex align-items-center gap-2">
                <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                  KPI bán hàng{' '}
                </span>
                <KTTooltip
                  text="Với đơn hàng nhượng quyền, liên kết: Doanh thu được tính dựa trên giá trị sản phẩm trụ sạc đã bán trước thuế, không tính giá trị trang thiết bị & dịch vụ có trong đơn hàng."
                  textHeader="KPI bán hàng"
                  isHtmlContent
                >
                  <i className="fa-regular fa-info-circle" style={{ color: AppColors.primary }} />
                </KTTooltip>
              </div>

              <KTFormInput
                name="month"
                type={KTFormInputType.month}
                onChange={(value) => {
                  setDateFilterMonthlySale(value);
                }}
                value={dateFilterMonthlySale}
                placeholder='MM/YYYY'
                additionalClassName='max-w-150px'
              />
            </div>

            <div
              className="d-flex flex-wrap flex-grow-1"
              style={{
                borderBottom: `1px solid ${AppColors.gray[300]}`,
              }}
            >
              <div
                className="w-50 d-flex flex-column align-items-center justify-content-center gap-2 py-4"
              >
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[700])}>
                  KPI Nhượng quyền
                </span>
                <div>
                  <HalfCircleChart
                    color="rgba(50, 173, 230, 1)"
                    value={(() => {
                      const target = agencyDetail?.kpis?.find(kpi => kpi?.orderType === AgencyConstants.AgencyRole.INVESTOR
                        && kpi?.month === dateFilterMonthlySale)?.kpiValue ?? 0;
                      const current = monthlySaleData[AgencyConstants.AgencyRole.INVESTOR]?.selfRevenue ?? 0;
                      return target > 0 ? Math.round((current / target) * 100) : 0;
                    })()}
                    unit="%"
                  />
                </div>
                <div className="d-flex align-items-center justify-content-around w-100 flex-wrap">
                  <div className="d-flex flex-column align-items-center">
                    <span style={UIHelper.buildFontStyle('500', '13px', AppColors.gray[700])}>
                      Hiện tại:
                    </span>
                    <span style={UIHelper.buildFontStyle('600', '13px', 'rgba(50, 173, 230, 1)')}>
                      {Utils.formatNumber(
                        monthlySaleData[AgencyConstants.AgencyRole.INVESTOR]?.selfRevenue ?? 0
                      )}
                    </span>
                  </div>
                  <div className="d-flex flex-column align-items-center">
                    <span style={UIHelper.buildFontStyle('500', '13px', AppColors.gray[700])}>
                      Mục tiêu:
                    </span>
                    <span style={UIHelper.buildFontStyle('600', '13px', AppColors.gray[800])}>
                      {Utils.formatNumber(
                        agencyDetail?.kpis?.find(kpi => kpi?.orderType === AgencyConstants.AgencyRole.INVESTOR
                          && kpi?.month === dateFilterMonthlySale)?.kpiValue ?? 0
                      )}
                    </span>
                  </div>
                </div>
              </div>
              <div className="w-50 d-flex flex-column align-items-center justify-content-center gap-2 py-4">
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[700])}>
                  KPI Bán liên kết
                </span>
                <div>
                  <HalfCircleChart
                    color="rgba(221, 75, 146, 1)"
                    value={(() => {
                      const target = agencyDetail?.kpis?.find(kpi => kpi?.orderType === AgencyConstants.AgencyRole.COINVESTOR
                        && kpi?.month === dateFilterMonthlySale)?.kpiValue ?? 0;
                      const current = monthlySaleData[AgencyConstants.AgencyRole.COINVESTOR]?.selfRevenue ?? 0;
                      return target > 0 ? Math.round((current / target) * 100) : 0;
                    })()}
                    unit="%"
                  />
                </div>
                <div className="d-flex align-items-center justify-content-around w-100 flex-wrap">
                  <div className="d-flex flex-column align-items-center">
                    <span style={UIHelper.buildFontStyle('500', '13px', AppColors.gray[700])}>
                      Hiện tại:
                    </span>
                    <span
                      style={UIHelper.buildFontStyle('600', '13px', 'rgba(221, 75, 146, 1)')}
                    >
                      {Utils.formatNumber(
                        monthlySaleData[AgencyConstants.AgencyRole.COINVESTOR]?.selfRevenue ?? 0
                      )}
                    </span>
                  </div>
                  <div className="d-flex flex-column align-items-center">
                    <span style={UIHelper.buildFontStyle('500', '13px', AppColors.gray[700])}>
                      Mục tiêu:
                    </span>
                    <span style={UIHelper.buildFontStyle('600', '13px', AppColors.gray[800])}>
                      {Utils.formatNumber(
                        agencyDetail?.kpis?.find(kpi => kpi?.orderType === AgencyConstants.AgencyRole.COINVESTOR
                          && kpi?.month === dateFilterMonthlySale)?.kpiValue ?? 0
                      )}
                    </span>
                  </div>
                </div>
              </div>
              {/* Tạm ẩn thẻ KPI role LANDLORD (bị trùng nhãn "bán liên kết" với COINVESTOR)
              <div className="w-50 d-flex flex-column align-items-center justify-content-center gap-2 py-4">
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[700])}>
                  KPI Chủ mặt bằng
                </span>
                <div>
                  <HalfCircleChart
                    color="rgba(221, 75, 146, 1)"
                    value={Math.round(
                      (monthlySaleData[AgencyConstants.AgencyRole.LANDLORD]?.saleKPI ?? 0)
                    )}
                    unit="%"
                  />
                </div>
                <div className="d-flex align-items-center justify-content-around w-100 flex-wrap">
                  <div className="d-flex flex-column align-items-center">
                    <span style={UIHelper.buildFontStyle('500', '13px', AppColors.gray[700])}>
                      Hiện tại:
                    </span>
                    <span style={UIHelper.buildFontStyle('600', '13px', 'rgba(221, 75, 146, 1)')}>
                      {Utils.formatNumber(
                        monthlySaleData[AgencyConstants.AgencyRole.LANDLORD]?.selfRevenue ?? 0
                      )}
                    </span>
                  </div>
                  <div className="d-flex flex-column align-items-center">
                    <span style={UIHelper.buildFontStyle('500', '13px', AppColors.gray[700])}>
                      Mục tiêu:
                    </span>
                    <span style={UIHelper.buildFontStyle('600', '13px', AppColors.gray[800])}>
                      {Utils.formatNumber(
                        agencyDetail?.kpis?.find(kpi => kpi?.orderType === AgencyConstants.AgencyRole.LANDLORD
                          && kpi?.month === dateFilterMonthlySale)?.kpiValue ?? 0
                      )}
                    </span>
                  </div>
                </div>
              </div>
              */}
            </div>

            <div className="d-flex flex-column gap-1 px-4 py-4">
              <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[700])}>
                KPI Tổng doanh thu
              </span>
              <KTBSProgressBar
                completed={
                  _.sum([
                    agencyDetail?.kpis?.find(kpi => kpi?.orderType === AgencyConstants.AgencyRole.INVESTOR
                      && kpi?.month === dateFilterMonthlySale)?.kpiValue ?? 0,
                    agencyDetail?.kpis?.find(kpi => kpi?.orderType === AgencyConstants.AgencyRole.COINVESTOR
                      && kpi?.month === dateFilterMonthlySale)?.kpiValue ?? 0,
                  ]) > 0
                    ? Math.round((_.sum([
                      monthlySaleData[AgencyConstants.AgencyRole.INVESTOR]?.selfRevenue ?? 0,
                      monthlySaleData[AgencyConstants.AgencyRole.COINVESTOR]?.selfRevenue ?? 0,
                    ]) /
                      _.sum([
                        agencyDetail?.kpis?.find(kpi => kpi?.orderType === AgencyConstants.AgencyRole.INVESTOR
                          && kpi?.month === dateFilterMonthlySale)?.kpiValue ?? 0,
                        agencyDetail?.kpis?.find(kpi => kpi?.orderType === AgencyConstants.AgencyRole.COINVESTOR
                          && kpi?.month === dateFilterMonthlySale)?.kpiValue ?? 0,
                      ])) * 100)
                    : 0
                }
                maxValue={100}
                bgColor={AppColors.primary}
                showValue={false}
              />
              <div className="d-flex align-items-center justify-content-between">
                <span style={UIHelper.buildFontStyle('600', '13px', AppColors.primary)}>
                  {Utils.formatNumber(
                    _.sum([
                      monthlySaleData[AgencyConstants.AgencyRole.INVESTOR]?.selfRevenue ?? 0,
                      monthlySaleData[AgencyConstants.AgencyRole.COINVESTOR]?.selfRevenue ?? 0,
                    ])
                  )}
                </span>
                <span style={UIHelper.buildFontStyle('600', '13px', 'rgba(42, 43, 75, 1)')}>
                  {Utils.formatNumber(
                    _.sum([
                      agencyDetail?.kpis?.find(kpi => kpi?.orderType === AgencyConstants.AgencyRole.INVESTOR
                        && kpi?.month === dateFilterMonthlySale)?.kpiValue ?? 0,
                      agencyDetail?.kpis?.find(kpi => kpi?.orderType === AgencyConstants.AgencyRole.COINVESTOR
                        && kpi?.month === dateFilterMonthlySale)?.kpiValue ?? 0,
                    ])
                  )}
                </span>
              </div>
            </div>
          </div>,
          false,
          false,
          true,
          true
        )}
      </div>
    )
  }, [monthlySaleData, agencyDetail])
  const commissionSaleCardEl = useMemo(() => {
    return (
      <div className='h-100'>
        {UIHelper.buildCard(
          <div>
            {UIHelper.buildCardHeader(
              'Hoa hồng đã nhận',
              false,
              <KTFormInput
                name="year"
                type={KTFormInputType.year}
                onChange={(value) => {
                  setDateFilterReconciliation(value);
                }}
                value={dateFilterReconciliation}
                placeholder='YYYY'
                additionalClassName='max-w-150px'
              />,
              false,
              true
            )}
            {/* table */}
            <div className="px-4 pb-4">
              {!_.isEmpty(formatSaleReconciliationList) ? (
                <div
                  className="table-responsive"
                  style={{
                    border: `1px solid ${AppColors.gray[300]}`,
                    borderRadius: '10px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                  }}
                >
                  <table className="table mb-0">
                    <thead>
                      <tr className="position-sticky sticky-top">
                        <th
                          className="align-top py-2"
                          style={{
                            ...UIHelper.buildFontStyle('600', '12px', AppColors.gray[900]),
                            backgroundColor: AppColors.gray[100],
                            borderBottom: `1px solid ${AppColors.gray[300]}`,
                          }}
                        >
                          Tháng
                        </th>
                        <th
                          className="text-right w-150px py-2"
                          style={{
                            ...UIHelper.buildFontStyle('600', '12px', AppColors.gray[900]),
                            backgroundColor: AppColors.gray[100],
                            borderBottom: `1px solid ${AppColors.gray[300]}`,
                          }}
                        >
                          <div className="d-flex flex-column">
                            <span
                              style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
                            >
                              Số tiền
                            </span>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {formatSaleReconciliationList?.map((item, index) => {
                        const isLastItem = index === formatSaleReconciliationList.length - 1;
                        return (
                          <tr key={`COMMISSION_RECEIVED_${item?.id}`}>
                            <td
                              style={{
                                ...UIHelper.buildFontStyle('400', '14px', AppColors.gray[600]),
                                borderBottom: isLastItem
                                  ? 'none'
                                  : `1px solid ${AppColors.gray[300]}`,
                              }}
                            >
                              <span>
                                Tháng {Utils.formatDateTimeToString(item?.month, 'MM/YYYY')}
                              </span>
                            </td>
                            <td
                              className="text-right"
                              style={{
                                ...UIHelper.buildFontStyle('500', '13px', AppColors.gray[700]),
                                borderBottom: isLastItem
                                  ? 'none'
                                  : `1px solid ${AppColors.gray[300]}`,
                              }}
                            >
                              {Utils.formatNumber(item?.netAmount ?? 0)} VNĐ
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div>
                  {UIHelper.buildEmptyNoData('Vui lòng thử lại hoặc kiểm tra kết nối mạng')}
                </div>
              )}
            </div>
          </div>,
          false,
          false,
          true,
          true
        )}
      </div>
    )
  }, [dateFilterReconciliation, formatSaleReconciliationList])

  // doi soat dai ly
  const [saleReconciliation, setSaleReconciliation] = useState([]);
  useEffect(() => {
    if (!agencyId) return;
    AgencyHelper.API.requestGetReconciliationList({
      agencyId: agencyId,
      forRole: AgencyConstants.AgencyRole.SALE,
    }).then((res) => {
      setSaleReconciliation(res?.reconciliations);
    });
  }, [agencyId]);

  const SaleReconciliationTable = (data) => {
    const textHeader = ['Tháng', 'Số tiền'];

    const contents = data?.map(item => ({
      name: `Tháng ${Utils.formatDateTimeToString(item?.month, 'MM/YYYY')}`,
      value: (item?.netAmount)
    })) || [];

    let html = `
    <div>
      Mức hoa hồng của những tháng chưa ký đối soát.
      <div class="row m-0 position-relative py-2 bg-white rounded-lg">
        <div class="rounded-lg p-0" style="border: 1px solid #EBEDEF">
          <table class="w-100" style="border-collapse: collapse">
            <thead>
              <tr style="background-color: #F7F7FD; font-size: 12px; color: #494968">
  `;

    textHeader.forEach((header, index) => {
      const align = index === 0 ? 'text-left' : 'text-right';
      const minWidth = index === 0 ? '200px' : '100px';
      html += `
      <th class="p-3 ${align}" style="min-width: ${minWidth}; font-weight: bold; padding: 12px 16px;">
        ${header}
      </th>`;
    });

    html += `
              </tr>
            </thead>
            <tbody>`;

    if (contents.length > 0) {
      contents.forEach(row => {
        html += `
      <tr style="border-top: 1px solid #EBEDEF">
        <td class="p-3 text-left" style="padding: 12px 16px;">${row.name ?? '--'}</td>
        <td class="p-3 text-right" style="padding: 12px 16px;">
          ${Utils.formatNumber(row.value)} VNĐ
        </td>
      </tr>`;
      });
    } else {
      html += `
      <tr>
        <td colspan="2" class="p-3 text-center">Không có dữ liệu để hiển thị</td>
      </tr>`;
    }

    html += `
            </tbody>
          </table>
        </div>
      </div>
    </div>`;

    return html;
  };

  return (
    <div className="mt-4">
      {/* header */}
      <div className="px-4">
        <AgencyDashboardHeaderView
          name={agencyDetail?.name}
          role={!user?.agency?.parentAgencyId ? "Tổng đại lý" : "Đại lý bán hàng"}
          contentEl={
            <div className="px-4 d-flex flex-column gap-3 mt-4">
              {UIHelper.buildStatisticItem(
                'rgba(88, 183, 198, 1)',
                <div className="d-flex align-items-center gap-2">
                  Tổng số đơn hàng trong tháng{' '}
                  <KTTooltip
                    overlayClassName="tooltip-content-card-sale"
                    text={`<div><span>Đơn hàng bán nhượng quyền: ${Utils.formatNumber(
                      monthlySaleData[AgencyConstants.AgencyRole.INVESTOR]?.selfCount ?? 0
                    )}</span><br /><span>Đơn hàng bán liên kết: ${Utils.formatNumber(
                      monthlySaleData[AgencyConstants.AgencyRole.COINVESTOR]?.selfCount ?? 0
                    )}</span></div>`}
                    textHeader="Số đơn hàng đã bán trong tháng"
                    isHtmlContent
                  >
                    <i className="fa-regular fa-info-circle" style={{ color: AppColors.primary }} />
                  </KTTooltip>
                </div>,
                Utils.formatNumber(
                  _.sum([
                    monthlySaleData[AgencyConstants.AgencyRole.INVESTOR]?.selfCount ?? 0,
                    monthlySaleData[AgencyConstants.AgencyRole.COINVESTOR]?.selfCount ?? 0,
                  ])
                ),
                null,
                'fa-solid fa-sack-dollar'
              )}
              {UIHelper.buildStatisticItem(
                'rgba(48, 189, 111, 1)',
                <div className="d-flex align-items-center gap-2">
                  Doanh thu bán hàng trong tháng{' '}
                  <KTTooltip
                    text="Với đơn hàng nhượng quyền, liên kết và bán trụ sạc: Doanh thu được tính dựa trên giá trị sản phẩm trụ sạc đã bán trước thuế, không tính giá trị trang thiết bị & dịch vụ có trong đơn hàng."
                    textHeader="Doanh thu bán hàng trong tháng"
                    isHtmlContent
                  >
                    <i className="fa-regular fa-info-circle" style={{ color: AppColors.primary }} />
                  </KTTooltip>
                </div>,
                `${Utils.formatNumber(
                  _.sum([
                    monthlySaleData[AgencyConstants.AgencyRole.INVESTOR]?.selfRevenue ?? 0,
                    monthlySaleData[AgencyConstants.AgencyRole.COINVESTOR]?.selfRevenue ?? 0,
                  ])
                )} VNĐ`,
                null,
                'fa-solid fa-chart-mixed'
              )}
              {UIHelper.buildStatisticItem(
                'rgba(255, 166, 63, 1)',
                <div className="d-flex align-items-center gap-2">
                  Tổng mức hoa hồng dự kiến{' '}
                  <KTTooltip
                    text={SaleReconciliationTable(
                      saleReconciliation?.filter(r => r?.status !== 'COMPLETED')
                    )}
                    textHeader="Hoa hồng dự kiến"
                    isHtmlContent
                  >
                    <i className="fa-regular fa-info-circle" style={{ color: AppColors.primary }} />
                  </KTTooltip>
                </div>,
                `${Utils.formatNumber(
                  _.sum([
                    monthlySaleDataCommission?.totalSaleCommission ?? 0,
                    monthlySaleDataCommission?.totalInvestorEnergyCommission ?? 0,
                    monthlySaleDataCommission?.totalCoinvestorCommission ?? 0,
                    monthlySaleDataCommission?.totalCoinvestorEnergyCommission ?? 0,
                  ])
                )} VNĐ`,
                null,
                'fa-solid fa-badge-percent'
              )}
            </div>
          }
          showSlide={isRoleSale}
          replaceContentEl={
            <div className="h-100">
              {chartRevenueEl}
            </div>
          }
        />
      </div>

      {/* thong ke */}
      <div className="mt-4">
        <div className="px-4">
          {!isAdminAgency && UIHelper.buildCard(
            <div className="w-100 overflow-x-auto">
              <div className="d-flex m-0 p-0">
                <div className="flex-grow-1 py-4 px-4 min-w-250px" style={{ borderRight: `1px solid ${AppColors.gray[200]}` }}>
                  {UIHelper.buildStatisticItem(
                    'rgba(50, 173, 230, 1)',
                    'Hoa hồng bán nhượng quyền',
                    `${Utils.formatNumber(
                      monthlySaleDataCommission?.totalSaleCommission ?? 0
                    )} VNĐ`,
                    null,
                    'fa-solid fa-charging-station'
                  )}
                </div>
                <div className="flex-grow-1 py-4 px-4 min-w-250px" style={{ borderRight: `1px solid ${AppColors.gray[200]}` }}>
                  {UIHelper.buildStatisticItem(
                    'rgba(48, 189, 111, 1)',
                    'Hoa hồng tiền điện nhượng quyền',
                    `${Utils.formatNumber(
                      monthlySaleDataCommission?.totalInvestorEnergyCommission ?? 0
                    )} VNĐ`,
                    null,
                    'fa-solid fa-bolt'
                  )}
                </div>
                <div className="flex-grow-1 py-4 px-4 min-w-250px" style={{ borderRight: `1px solid ${AppColors.gray[200]}` }}>
                  {UIHelper.buildStatisticItem(
                    'rgba(221, 75, 146, 1)',
                    'Hoa hồng bán liên kết',
                    `${Utils.formatNumber(
                      monthlySaleDataCommission?.totalCoinvestorCommission ?? 0
                    )} VNĐ`,
                    AppResource.icons.tmt.icChartNetwork,
                    null
                  )}
                </div>
                <div className="flex-grow-1 py-4 px-4 min-w-250px">
                  {UIHelper.buildStatisticItem(
                    'rgba(66, 135, 209, 1)',
                    'Hoa hồng tiền điện liên kết',
                    `${Utils.formatNumber(
                      monthlySaleDataCommission?.totalCoinvestorEnergyCommission ?? 0
                    )} VNĐ`,
                    AppResource.icons.tmt.icHexagonNodesBolt,
                    null
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="mt-4 row m-0 p-0 px-2">
        {/* doanh thu ban hang */}
        {isRoleSale && (
          <div className='col-lg-6 px-2 mb-4'>
            {chartRevenueEl}
          </div>
        )}
        {/* thong ke NDT/chu mat bang */}
        {isRoleSale && (
          <div className="col-lg-6 mb-4 px-2">
            {agencyStatisticCardEl}
          </div>
        )}
        {isRoleSale && !isAdminAgency && (
          <div className='col-lg-6 px-2 mb-4'>
            {kpiSaleCardEl}
          </div>
        )}
        {/* hoa hong da nhan */}
        {!isAdminAgency && (
          <div className="col-lg-6 mb-4 px-2">
            {commissionSaleCardEl}
          </div>
        )}
        {/* thong ke NDT/chu mat bang */}
        {!isRoleSale && (
          <div className="col-lg-6 mb-4 px-2">
            {agencyStatisticCardEl}
          </div>
        )}
      </div>
    </div>
  );
}

export default SaleDashboardScreen;
